import './cookie-preference.css';

import EventEmitter from 'eventemitter3';
import { EventTypes } from '../../../core/event-bus';
import { Util } from "../../../utils/util";
import { AppPlatform, CookiePreferences } from '../../../types/enums';
import { Modal } from "../modal";
import { Data } from '../../../models/data';
import { Context } from "../../../models/context";
import { LocalizationService } from "../../../services/localization-service";
import { DataLayerService } from '../../../services/data-layer-service';

export class CookiePreference extends Modal {
    private readonly _data: Data;
    private readonly _cookieKey: string = "cookiePreference";
    private readonly _cookieRetention: number = 6; // months

    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, dataLayer: DataLayerService) {
        super("modal_cookie_preference", eventBus, context, dataLayer, localizationService, {
            hasCloseButton: false,
            hasCloseOnBlur: false,
            disableDimmer: false,
            disableEscapeClose: true,
            ariaLabel: "Cookie Preference"
        });
        this._data = data;

        this._eventBusListeners();
    }

    /**
     * Initializes the modal and checks if an existing cookie preference exists.
     */
    public init = (): void => {
        super.init();
        this._setEventListeners();
    }

    /**
     * Sets the event listeners for the buttons in the modal.
     */
    protected readonly _setEventListeners = (): void => {
        Util.setElement("onclick", "btn_cookie_preference_accept", async () => {
            await this._onAcceptClickAsync();
        });

        Util.setElement("onclick", "btn_cookie_preference_reject", this._onDenyClick);
    }

    /**
     * Sets the event bus listeners when brand data is retrieved. If the cookie does not yet exist, it will set a listener to
     * show the popup. If the cookie does exist and the pereference is to "accept", non-essential scripts will be loaded
     */
    private _eventBusListeners() {
        const cookie = Util.getCookie(this._cookieKey);
        const hasAcceptedCookies = !!cookie && cookie === CookiePreferences.ACCEPT;
        const hasRejectedCookies = !!cookie && cookie === CookiePreferences.REJECT;
        if (hasRejectedCookies)
            return;

        this._eventBus.on(EventTypes.BRAND_DATA_RETRIEVED, async () => {
            if (hasAcceptedCookies) {
                await this._runNonEssentialScriptsAsync();
                return;
            }

            this.open();
        });
    }

    /**
     * Handles the acceptance of cookies.
     * 
     * - If the platform is iOS and tracking is disabled (`isTrack === false`), it behaves as a denial.
     * - Otherwise, it sets the cookie preference to ACCEPT, runs any non-essential scripts, and closes the dialog.
     * 
     * @returns {Promise<void>}
     */
    private readonly _onAcceptClickAsync = async (): Promise<void> => {
        const isTrackEnabled = this._context.getProperty("isTrack");
        const platform = this._context.getProperty("platform");

        if (!isTrackEnabled && platform === AppPlatform.IOS) {
            this._onDenyClick();
            return;
        }

        this._setCookiePreference(CookiePreferences.ACCEPT);
        Util.setConsent(true);
        await this._runNonEssentialScriptsAsync();
        this.close();
    }

    /**
     * Handles the denial of cookies.
     * 
     * - Sets the cookie preference to REJECT.
     * - Deletes the `locationData` cookie scoped to the top-level domain.
     * - Closes the dialog.
     */
    private readonly _onDenyClick = (): void => {
        this._setCookiePreference(CookiePreferences.REJECT);
        Util.setConsent(false);
        const domain = window.location.hostname.split('.').reverse().slice(0, 2).reverse().join('.');
        Util.deleteCookie("locationData", domain);
        this.close();
    }

    /**
     * Sets the cookie preference. The cookie will be set to expire in 6 months.
     * @param preference - The cookie preference to set. Can be either "accept" or "reject".
     */
    private readonly _setCookiePreference = (preference: CookiePreferences): void => {
        const expires = new Date();
        expires.setMonth(expires.getMonth() + this._cookieRetention);
        const days = Util.getDaysUntilExpiry(expires.toUTCString());
        Util.setCookie(this._cookieKey, preference, days);
        this._eventBus.emit(EventTypes.COOKIE_PREFERENCE, preference);
    }

    /**
     * Loads the non-essential scripts. This is called either when a cookie preference is set to "accept"
     * and brand data has been retrieved, or when the user explicitly accepts the cookie preference.
     */
    private readonly _runNonEssentialScriptsAsync = async (): Promise<void> => {
        const contentFolderUrl = this._context.getProperty("publicStorageUrl");
        const nonEssentialUrl = `${contentFolderUrl}/online/brand-non-essential.js`;

        await Util.loadScriptAsync(nonEssentialUrl)
            .catch((err) => {
                console.error("Non-essential scripts failed to load:", err);
            });
    }
}